/**
 * INCSUBpoolMemberDetailCard.jsx
 *
 * This component renders:
 * - A list of members in a given pool with details like name, join date, price, refund status, and action.
 * - Pool verification details like status, credentials, and verified-by information.
 *
 * @author vishal lohbande
 */

import React, { useState } from 'react';
import './poolMemberDetailCard.css';
import INCSUBpoolMemeberrow from './poolMemeberrow';

/**
 * INCSUBpoolMemberDetailCard Component
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.pool - Pool object containing member and verification details
 * @param {Function} props.fun - Callback function triggered for member actions (e.g. suspend)
 * 
 * @returns {JSX.Element} Rendered card with member list and verification details
 */
function INCSUBpoolMemberDetailCard({ pool, fun }) {

    /**
     * Renders the list of member rows from the pool's member detail list.
     *
     * @function
     * @returns {JSX.Element[]} List of member rows
     */
    const renderedMemberDetailRow = pool && pool.poolMemberDetailsList.length > 0 &&
        pool.poolMemberDetailsList.map((poolMember) => {
            return (
                <INCSUBpoolMemeberrow
                    key={poolMember.memberId}
                    member={poolMember}
                    fun={fun}
                    pool={pool}
                />
            );
        });
        
    /**
     * Formats a date string to 'DD Month YYYY' format (e.g., 15 May 2025)
     *
     * @function
     * @param {string} dateString - Date string to format
     * @returns {string} Formatted date
     *
     * @author govind dindewad
     */
   const formateDate = (dateString) => {
        const date = new Date(dateString);

        return new Intl.DateTimeFormat("en-GB", {
            day: "2-digit",
            month: 'short',
            year: "numeric"
        }).format(date);
    };

    return (
        <div className='INCSUBPoolMemberDetail_ctn_main'>

            {/* Left section: Member details list */}
            {pool && pool.poolMemberDetailsList.length > 0 ? (
                <div className='INCSUBPoolMemberDetail_ctn'>
                    <div className='INCSUBPoolMemberDetail_heading'>Member Details</div>
                    <div className='INCSUBPoolMemberDetail_table_container'>
                    <div className='INCSUBPoolMemberDetail_headrow'>
                        <div className='INCSUBPoolMemberDetail_headrow_name'>Name</div>
                        <div className='INCSUBPoolMemberDetail_headrow_joinindate'>Pool Joining Date</div>
                        <div className='INCSUBPoolMemberDetail_headrow_planprice'>Plan Price</div>
                        <div className='INCSUBPoolMemberDetail_headrow_refund'>Refund</div>
                        <div className='INCSUBPoolMemberDetail_headrow_Action'>Action</div>
                    </div>
                    {renderedMemberDetailRow}
                    </div>
                </div>
            ) : (
                <div className='INCSUBPoolMemberDetail_ctn'>
                    <div className='INCSUBPoolMemberDetail_ctn_empty'>No members joined yet</div>
                </div>
            )}

            {/* Right section: Verification details */}
            <div className='INCSUBPoolMemberDetail_ctn_right'>
                <div className='INCSUBPoolVerification_heading'>Verification Details</div>

                {/* Verification status */}
                <div className='INCSUBPoolVerification_status_activebtn'>
                    <div className='INCSUBPoolVerification_status_heading'>
                        <div className='INCSUBPoolVerification_status_heading_disc'></div>
                        Verification Status:
                    </div>
                    <div className={`INCSUBPoolInfo_active_status ${pool.poolVerificationStatus}`}>
                        <div className='INCSUBPoolInfo_active_status_disc'></div>
                        <div className='INCSUBPoolInfo_active_status_text'>{pool.poolVerificationStatus}</div>
                    </div>
                </div>

                {/* Credentials */}
                <div className='INCSUBPoolVerification_Credential_ctn'>
                    <div className='INCSUBPoolVerification_Credentials_heading'>
                        <div className='INCSUBPoolVerification_status_heading_disc'></div>
                        Credentials:
                    </div>
                    <div className='INCSUBPoolVerification_Credentials_hld'>
                        {!pool?.mobileNumberForVerification?<><div className='INCSUBPoolVerification_Credentials_pass_userId'>
                            <div className='INCSUBPoolVerification_Credentials_userId_head'>User Id</div>
                            <div className='INCSUBPoolVerification_Credentials_userId'>{pool.usernameForVerification}</div>
                        </div>
                        <div className='INCSUBPoolVerification_Credentials_pass_userId'>
                            <div className='INCSUBPoolVerification_Credentials_userId_head'>Password</div>
                            <div className='INCSUBPoolVerification_Credentials_userId'>{pool.passwordForVerification}</div>
                        </div></>:<div className='INCSUBPoolVerification_Credentials_pass_userId'>
                            <div className='INCSUBPoolVerification_Credentials_userId_head'>Mobile No</div>
                            <div className='INCSUBPoolVerification_Credentials_userId'>{pool.mobileNumberForVerification}</div>
                        </div>}
                        
                    </div>
                </div>

                {/* Verified by */}
                <div className='INCSUBPoolVerification_verifiedby_ctn'>
                    <div className='INCSUBPoolVerification_verifiedby_heading'>
                        <div className='INCSUBPoolVerification_status_heading_disc'></div>
                        Verified By:
                    </div>
                    <div className='INCSUBPoolVerification_verifiedby_imgname'>
                        <div className='INCSUBPoolVerification_verifiedby_imgname_hld'>
                            {pool.verifiedByImage && (
                                <div className='INCSUB_UserImg'>
                                    <img
                                        className='INCSUB_UserImgTag'
                                        src={pool.verifiedByImage || '/assets/icons/common/defaultImage.png'}
                                        alt='default img'
                                    />
                                </div>
                            )}
                            <div className='INCSUBPoolVerification_verifiedby_imgnametext'>
                                <p className='INCSUBRolemanagment_name'>{pool.verifiedByName}</p>
                                <p className='INCSUBRolemanagment_no'>{pool.verifiedByRole}</p>
                            </div>
                        </div>
                        <div className='INCSUBPoolVerification_verifiedby_date'>
                            {pool.verificationDate?formateDate(pool.verificationDate):"-"}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default INCSUBpoolMemberDetailCard;
